import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  BookOpen,
  Clock,
  MonitorPlay,
  ShoppingBasket,
  ShoppingCart,
  UserPlus,
} from "lucide-react";
import { CourseCardProps } from "../types/coursesDTO";
import { Link } from "react-router-dom";
import { ConstantHelper } from "@/services/helpers/constantHelper.ts";
import { TimeHelper } from "@/services/helpers/timeHelpers.ts";
import { useTranslation } from "react-i18next";
import { sanitizeHtml } from "@/lib/sanitize";

export function CourseCard({ course, isAuthenticated, onAddToBasket }: CourseCardProps) {
  const { t } = useTranslation();
  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
      <Link to={`/course/${course.id}`}>
        <CardContent className="p-0">
          <div className="flex flex-col md:flex-row">
            {/* Course Image */}
            <div className="p-6">
              <div className="w-full md:w-80 lg:w-96 flex-shrink-0">
                <div className="aspect-video md:aspect-[4/3] lg:aspect-video bg-muted flex items-center justify-center rounded-lg">
                  <img
                    src={
                      course.image || "/placeholder.svg?height=200&width=400"
                    }
                    alt={course.name}
                    className="w-full h-full object-cover rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Course Content */}
            <div className="flex-1 p-4 md:p-6">
              <div className="flex flex-col h-full">
                {/* Course Title and Level */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                  <h3 className="text-lg md:text-xl font-semibold text-gray-900 line-clamp-2 flex-1">
                    {course.name}
                  </h3>
                  <Badge
                    className={`${ConstantHelper.getLevelColor(
                      course.level,
                    )} flex-shrink-0 self-start sm:self-center`}
                  >
                    {ConstantHelper.getLevelText(course.level)}
                  </Badge>
                </div>

                {/* Course Description */}
                <div
                  className="text-gray-600 text-sm md:text-base mb-4 line-clamp-2 md:line-clamp-3 course-description-preview"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(course.description ?? ""),
                  }}
                />

                {/* Course Details */}
                <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>
                      {TimeHelper.convertMinuteToText(course.durationMinute)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <BookOpen className="h-4 w-4" />
                    <span>
                      {course.totalLessons} {t("course.lessons")}
                    </span>
                  </div>
                </div>

                {/* Price and Add to Basket */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-auto">
                  <div className="flex items-center gap-2 flex-wrap">
                    {course.price !== course.discountedPrice ? (
                      <>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-xl md:text-2xl font-bold text-orange-500 cursor-default">
                                {course.discountedPriceText}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t("course.discountTooltip")}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <span className="text-sm md:text-base font-medium line-through text-gray-400">
                          {course.priceText}
                        </span>
                        <Badge className="bg-orange-500 hover:bg-orange-500 text-white text-xs px-2 py-0.5">
                          %{Math.round((1 - course.discountedPrice / course.price) * 100)} {t("course.off")}
                        </Badge>
                      </>
                    ) : (
                      <span className="text-xl md:text-2xl font-bold text-gray-900">
                        {course.priceText}
                      </span>
                    )}
                  </div>

                  <div className="flex-shrink-0">
                    {course.isCoursePurchased ? (
                      <Button className="w-full sm:w-auto flex items-center gap-2">
                        <MonitorPlay className="h-4 w-4" />
                        <span>{t("course.goToCourse")}</span>
                      </Button>
                    ) : !isAuthenticated ? (
                      <Link to="/register" onClick={(e) => e.stopPropagation()}>
                        <Button className="w-full sm:w-auto flex items-center gap-2">
                          <UserPlus className="h-4 w-4" />
                          <span>{t("auth.register")}</span>
                        </Button>
                      </Link>
                    ) : course.isInTheBasket ? (
                      <Link to="/basket" onClick={(e) => e.stopPropagation()}>
                        <Button className="w-full sm:w-auto flex items-center gap-2">
                          <ShoppingBasket className="h-4 w-4" />
                          <span>{t("common.goToBasket")}</span>
                        </Button>
                      </Link>
                    ) : (
                      <Button
                        onClick={(e) => {
                          e.preventDefault();
                          onAddToBasket?.(course.id);
                        }}
                        className="w-full sm:w-auto flex items-center gap-2"
                      >
                        <ShoppingCart className="h-4 w-4" />
                        <span>{t("common.addToBasket")}</span>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Link>
    </Card>
  );
}
